import { setVideoFormat } from '@/player/videoFormat';
import motion from './appearanceMotion';

// Only the Add video workflow sets this marker. Dates, IDs and source type are
// deliberately not used: an old video remains old after editing or cloning.
export const isModernVideo = model => model?.settings?.editor_version === 2;
export const THEMES = [
  { id: 'default', label: 'Default', primary: '#4b5563', secondary: '#6b7280', skin: 'vjs-theme-default', shape: 'circle' },
  { id: 'youtube', label: 'YouTube', primary: '#ff0000', secondary: '#ff4444', skin: 'vjs-theme-youtube', shape: 'rounded' },
  { id: 'vimeo', label: 'Vimeo', primary: '#1ab7ea', secondary: '#4dc9f0', skin: 'vjs-theme-vimeo', shape: 'rounded' },
  { id: 'wistia', label: 'Wistia', primary: '#7c3aed', secondary: '#a78bfa', skin: 'vjs-theme-wistia', shape: 'square' },
  { id: 'custom', label: 'Custom', primary: '#7c3aed', secondary: '#a78bfa', skin: 'vjs-theme-vimeo', shape: 'rounded' },
];
const speaker = '<path d="m11 5-6 4H2v6h3l6 4V5Z"/>';
const volume = speaker + '<path d="M15.54 8.46a5 5 0 0 1 0 7.08M19.07 4.93a10 10 0 0 1 0 14.14"/>';
const muted = speaker + '<path d="m23 9-6 6m0-6 6 6"/>';
export const SOUND_DESIGNS = [
  { id: 'speaker', label: 'Speaker Icon', icon: volume, text: '' },
  { id: 'speaker-muted', label: 'Muted Speaker', icon: muted, text: '' },
  { id: 'click-for-sound', label: 'Click for Sound', icon: volume, text: 'Click for Sound' },
  { id: 'tap-to-play', label: 'Tap to Play', icon: '<path d="M18 11V7a2 2 0 0 0-4 0v4M14 10V5a2 2 0 0 0-4 0v6M10 10.5V7a2 2 0 0 0-4 0v9l-1.5-1.5a2.12 2.12 0 0 0-3 3l3.9 3.9A5.5 5.5 0 0 0 9.3 23H14a8 8 0 0 0 8-8v-4a2 2 0 0 0-4 0Z"/>', text: 'Tap to Play' },
  { id: 'click-to-play', label: 'Click to Play', icon: '<path d="m4 4 7.07 17 2.51-7.39L21 11.07 4 4Z"/>', text: 'Click to Play' },
  { id: 'unmute', label: 'Unmute', icon: muted, text: 'Unmute' },
];
export const MODERN_ANIMATIONS = ['none', 'pulse', 'tada', 'zoomIn', 'jello', 'wobble', 'swing', 'bounceIn', 'bounce', 'flash', 'headShake', 'heartbeat', 'shakeX', 'shakeY'];
const safeColor = color => /^(#[\da-f]{3,8}|rgba?\([\d\s.,%]+\))$/i.test(color || '') ? color : '#7c3aed';
const image = (body, width, height, animation = 'none') => {
  const name = MODERN_ANIMATIONS.includes(animation) ? animation : 'none';
  const duration = { pulse: '4s', zoomIn: '1s', bounceIn: '1.5s', heartbeat: '2.6s' }[name] || '2s';
  const frames = name === 'pulse' ? 'vpPulse' : name;
  const keyframes = motion.split('@keyframes ').find(part => part.startsWith(frames + ' '));
  const animationCss = name === 'none' || !keyframes ? '' : '@keyframes ' + keyframes;
  const repeat = ['zoomIn', 'bounceIn'].includes(name) ? '1' : 'infinite';
  // SVG is an image, never injected HTML. It carries its motion to the existing
  // custom-image renderer used by both the editor and published embeds.
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><style>${animationCss}.motion{transform-box:fill-box;transform-origin:center;animation:${frames} ${duration} ease-in-out ${repeat}}@media(prefers-reduced-motion:reduce){.motion{animation:none}}</style><g class="motion">${body}</g></svg>`).replace(/'/g, '%27');
};
export function soundImage(designId, size = 'regular', animation = 'none') {
  const design = SOUND_DESIGNS.find(item => item.id === designId) || SOUND_DESIGNS[0];
  const scale = { small: .75, regular: 1, large: 1.25 }[size] || 1;
  const pad = (design.text ? 12 : 16) * scale;
  const icon = 32 * scale;
  const height = icon + pad * 2;
  const width = pad * 2 + icon + (design.text ? 8 + design.text.length * 7.4 * scale : 0);
  // All three sizes share a 96px image canvas, the existing player's huge
  // image size. The drawing itself retains VideoPlayer's 0.75/1/1.25 scale.
  const body = `<rect x="16" y="16" width="${width}" height="${height}" rx="8" fill="black" fill-opacity=".6"/><svg x="${16+pad}" y="${16+pad}" width="${icon}" height="${icon}" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${design.icon}</svg>${design.text ? `<text x="${16+pad+icon+8}" y="${16+height/2}" dominant-baseline="central" font-family="Inter,Arial,sans-serif" font-weight="500" font-size="${14*scale}" fill="white">${design.text}</text>` : ''}`;
  return image(body, width + 32, 96, animation);
}
export function playImage(themeId, primary, animation = 'none') {
  const theme = THEMES.find(item => item.id === themeId) || THEMES[0];
  const rect = theme.shape === 'circle'
    ? '<circle cx="64" cy="64" r="44"'
    : `<rect x="${theme.shape === 'square' ? 20 : 10}" y="${theme.shape === 'square' ? 20 : 30}" width="${theme.shape === 'square' ? 88 : 108}" height="${theme.shape === 'square' ? 88 : 68}" rx="${theme.shape === 'square' ? 0 : 12}"`;
  return image(`${rect} fill="${safeColor(primary)}"/><path d="m54 44 30 20-30 20Z" fill="white"/>`, 128, 128, animation);
}
export function modernDefaults() {
  return { theme: 'default', soundDesign: 'speaker', soundSize: 'regular', soundAnimation: 'tada', playAnimation: 'tada', playSize: 'medium' };
}
// The API's read resource projects known fields, omitting extra settings keys.
// Carry editor-only metadata in our own SVG images, which it returns intact.
// This is inert image metadata, not executable markup or a source/date migration.
const withAppearance = (source, appearance) => {
  const metadata = encodeURIComponent(JSON.stringify({ version: 2, ...appearance }));
  return source.replace('%3Csvg%20', '%3Csvg%20data-groove-appearance%3D%22' + encodeURIComponent(metadata) + '%22%20');
};
export function restoreModernAppearance(model, set = (target, key, value) => { target[key] = value; }) {
  if (isModernVideo(model)) return model;
  const sources = [model.play_button?.custom_source, model.settings?.autoplay_settings?.custom_image];
  for (const source of sources) {
    if (typeof source !== 'string' || !source.startsWith('data:image/svg+xml;charset=utf-8,')) continue;
    try {
      const match = decodeURIComponent(source).match(/<svg data-groove-appearance="([^"]+)"/);
      if (!match) continue;
      const value = JSON.parse(decodeURIComponent(match[1]));
      const choices = {
        theme: THEMES.map(item => item.id), soundDesign: SOUND_DESIGNS.map(item => item.id),
        soundSize: ['small', 'regular', 'large'], soundAnimation: MODERN_ANIMATIONS,
        playAnimation: MODERN_ANIMATIONS, playSize: ['small', 'medium', 'large'],
      };
      if (value.version !== 2 || !Object.keys(choices).every(key => choices[key].includes(value[key]))) continue;
      const appearance = {};
      Object.keys(choices).forEach(key => { appearance[key] = value[key]; });
      set(model.settings, 'appearance', appearance);
      set(model.settings, 'editor_version', 2);
      return model;
    } catch (_) { /* An unrelated or malformed custom image stays legacy. */ }
  }
  return model;
}
export function usesModernAutoplay(model) {
  // Public player responses also omit editor-only fields. Read our persisted
  // metadata without changing the model or the legacy playback settings.
  const restored = { ...model, settings: { ...model.settings } };
  restoreModernAppearance(restored);
  return isModernVideo(restored) && [true, 1, '1'].includes(model.settings?.autoplay);
}
export function syncModernAppearance(model) {
  if (!isModernVideo(model)) return;
  const appearance = model.settings.appearance;
  const theme = THEMES.find(item => item.id === appearance.theme) || THEMES[0];
  model.settings.theme = theme.skin;
  model.settings.autoplay_settings.type = 2;
  model.settings.autoplay_settings.custom_image = withAppearance(soundImage(appearance.soundDesign, appearance.soundSize, appearance.soundAnimation), appearance);
  model.settings.unmute_size = 4;
  model.settings.unmute_animation = 'none'; // Motion travels inside the image.
  model.play_button.type = 3;
  model.play_button.custom_source = withAppearance(playImage(appearance.theme, model.settings.color.primary, appearance.playAnimation), appearance);
  model.play_button.animation = 'none';
  model.play_button.size = { small: 1, medium: 2, large: 3 }[appearance.playSize] || 2;
}
export function initializeModernVideo(model) {
  setVideoFormat(model, '16:9');
  model.settings = { ...model.settings, editor_version: 2, appearance: modernDefaults(), color: { primary: '#4b5563', secondary: '#ffffff' } };
  model.play_button = { ...model.play_button, enable: true, show_on_pause: true };
  syncModernAppearance(model);
  return model;
}
